/**
 * Training Record Service Module
 * 
 * Provides API functions for managing training record operations including
 * updating training records, deleting records, and handling training attendance.
 * 
 * @module TrainingRecordService
 * @author Welding Management System
 * @version 1.0.0
 */
import axios from "axios";

// ============================================================================
// API CONFIGURATION
// ============================================================================

/**
 * Configure API URL based on environment
 * @type {string}
 */
let MASTER_API_URL = "";
if (process.env.REACT_APP_NODE_ENV === "local") {
  MASTER_API_URL = process.env.REACT_APP_TRAINING_API_URL;
} else {
  MASTER_API_URL = process.env.REACT_APP_API_URL + "training";
}

// ============================================================================
// COMMENTED OUT FUNCTIONS (UNUSED)
// ============================================================================

// ============================================================================
// ACTIVE FUNCTIONS
// ============================================================================

/**
 * Update training record - ACTIVELY USED
 * 
 * Updates an existing training record with new information such as
 * attendance status, completion status, or scores.
 * 
 * Used in: AddEditAttendanceDetails.jsx, TrainingRecord.jsx
 * 
 * @async
 * @function updateTrainingRecord
 * @param {Object} skid - Training record identifier and update data
 * @param {string} skid.SK - Unique identifier for the training record
 * @param {string} [skid.attendanceStatus] - Updated attendance status
 * @param {number} [skid.score] - Updated training score
 * @param {string} [skid.completionDate] - Training completion date
 * @returns {Promise<Object>} Response data with update status
 * @throws {Object} Returns error object with message and status on failure
 * 
 * @example
 * const updateData = {
 *   SK: 'training-record-123',
 *   attendanceStatus: 'Present',
 *   score: 85,
 *   completionDate: '2024-01-15'
 * };
 * const result = await updateTrainingRecord(updateData);
 */
export async function updateTrainingRecord(skid) {
  let url = `${MASTER_API_URL}/trainRecord/update`;
  const response = await axios
    .post(url, skid)
    .then(function (resp) {
      return resp;
    })
    .catch(function (error) {
      return { message: "Unable to find request", status: false };
    });
  return response.data;
}

/**
 * Delete training record - ACTIVELY USED
 * 
 * Permanently removes a training record from the system.
 * This operation cannot be undone.
 * 
 * Used in: TrainingRecord.jsx
 * 
 * @async
 * @function DeleteTrainingRecord
 * @param {Object} training - Training record to delete
 * @param {string} training.SK - Unique identifier for the training record
 * @param {string} [training.PK] - Primary key for the training record
 * @returns {Promise<Object>} Response object with deletion status
 * @throws {string} Returns error message on failure
 * 
 * @example
 * const trainingToDelete = {
 *   SK: 'training-record-123',
 *   PK: 'training-2024'
 * };
 * const result = await DeleteTrainingRecord(trainingToDelete);
 */
export const DeleteTrainingRecord = async (training) => {
  let url = `${MASTER_API_URL}/delete`;
  const resp = await axios
    .post(url, training)
    .then(function (response) {
      return response;
    })
    .catch(function (error) {
      return error.message;
    });
  return resp;
};

// ============================================================================
// END OF SERVICE MODULE
// ============================================================================

